import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { SONOS_BLUE, sonosTextButtonTapSx } from './sonosUiStyles';

interface SonosConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'destructive' | 'primary';
  /** Sin velo oscuro detrás (evita opacar toda la pantalla). */
  hideBackdrop?: boolean;
  historyKey?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const SonosConfirmDialog: React.FC<SonosConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'destructive',
  hideBackdrop = true,
  historyKey = 'sonos-confirm-dialog',
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const requestCancel = useOverlayBackButton(open, onCancel, historyKey);
  const confirmSx = confirmVariant === 'primary'
    ? {
        bgcolor: SONOS_BLUE,
        '&:hover': { bgcolor: '#1a7ae0', boxShadow: 'none' },
      }
    : {
        bgcolor: '#e53935',
        '&:hover': { bgcolor: '#c62828', boxShadow: 'none' },
      };

  return (
    <Dialog
      open={open}
      onClose={requestCancel}
      hideBackdrop={hideBackdrop}
      sx={{ zIndex: 1800 }}
      slotProps={{ backdrop: { sx: { zIndex: 1799 } } }}
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          color: '#fff',
          borderRadius: 2,
          border: '1px solid #444',
          borderTop: `2px solid ${SONOS_BLUE}`,
          width: '100%',
          maxWidth: 300,
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{
        fontSize: 14,
        fontWeight: 700,
        color: SONOS_BLUE,
        pb: 0.5,
        pt: 1.5,
        px: 2,
      }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 0.75 }}>
        <Typography sx={{ fontSize: 13, color: '#ccc', lineHeight: 1.45 }}>
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5, pt: 0.25, gap: 0.5 }}>
        <Button
          onClick={requestCancel}
          size="small"
          disableRipple
          sx={{ color: '#999', textTransform: 'none', fontSize: 13, minWidth: 0, ...sonosTextButtonTapSx }}
        >
          {cancelLabel ?? t('common.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          size="small"
          variant="contained"
          disableRipple
          sx={{
            textTransform: 'none',
            fontSize: 13,
            boxShadow: 'none',
            '&:active': { transform: 'scale(0.97)' },
            ...confirmSx,
          }}
        >
          {confirmLabel ?? t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SonosConfirmDialog;
